import { gsap } from "gsap";
import { Container, FederatedPointerEvent, IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { StringFormatExtention } from "../../../../../extensions/StringFormatExtention";
import { ISettingsController } from "../../../../settings/ISettingsController";
import { GameFont } from "../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { Event } from "../../../../../shared/event-system/Event";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
type Timeline = gsap.core.Timeline;

export class CoinsField extends Container {
    private static readonly COLLECT_COINS_ANIMATION_TIME = 2.5;

    public get onPlusButtonClick(): Event<ButtonWithScaleAnimation, FederatedPointerEvent> {
        return this.plusButton.onUp;
    }
    private readonly commonResources: CommonResourcesPackage;
    private readonly settingsController: ISettingsController;
    private readonly logger: ILogger;

    private background: Sprite;
    private icon: Sprite;
    private textField: Text;
    private currentCoins: number;
    private coinsAnimation: Timeline;
    private plusButton: ButtonWithScaleAnimation;

    public constructor(
        commonResources: CommonResourcesPackage,
        value: number = 0,
        settingsController: ISettingsController,
        logger: ILogger
    ) {
        super();
        this.commonResources = commonResources;
        this.settingsController = settingsController;
        this.logger = logger;
        this.currentCoins = value;
        this.coinsAnimation = new gsap.core.Timeline();
        this.build();
        this.layout();
    }

    private build(): void {
        this.background = new Sprite(this.commonResources.assets_common_ui_top_panel_coins_field_png);
        this.addChild(this.background);
        this.icon = new Sprite(this.commonResources.assets_common_ui_top_panel_coin_png);
        this.addChild(this.icon);
        this.plusButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_top_panel_plus_button_png);
        this.addChild(this.plusButton);
        this.textField = new Text(StringFormatExtention.formatWithComma(this.currentCoins), new CoinsFieldTextStyle());
        this.addChild(this.textField);
        this.eventMode = "static";
        this.on("pointerup", this.plusButtonClickHandle, this)
    }

    private layout(): void {
        this.icon.position.set(8, (this.background.height - this.icon.height) * 0.5);
        if (this.textField.width >= this.width * 0.45) {
            this.textField.scale.set(this.width * 0.45 / (this.textField.width));
        }
        this.textField.position.set((this.width - this.textField.width) * 0.5 + 10, (this.background.height - this.textField.height + 3) * 0.5);
        this.plusButton.position.set(198, (this.background.height - this.plusButton.height) * 0.5);
    }

    public async updateCoins(value: number): Promise<void> {
        const target = { value: this.currentCoins };
        this.currentCoins = value;
        this.coinsAnimation.clear();
        await this.coinsAnimation.to(target, { value: this.currentCoins, onUpdate: () => this.updateCoinsText(target.value), duration: CoinsField.COLLECT_COINS_ANIMATION_TIME, ease: "power1.in" });
    }

    private updateCoinsText(value: number): void {
        this.textField.text = StringFormatExtention.formatWithComma(Math.round(value));
        if (this.textField.width >= this.width * 0.45) {
            this.textField.scale.set(this.width * 0.45 / (this.textField.width));
        }
        this.textField.position.set((this.width - this.textField.width) * 0.5 + 10, (this.background.height - this.textField.height + 3) * 0.5);
    }

    private async plusButtonClickHandle(e: FederatedPointerEvent): Promise<void> {
        try {
            this.plusButton.onUp.emit(this.plusButton, e);
            await this.settingsController.showShopPopup();
        } catch (error: unknown) {
            this.logger.error(error);
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.coinsAnimation?.kill();
        this.off("pointerup", this.plusButtonClickHandle, this)
        super.destroy(options);
    }
}

class CoinsFieldTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36,
            miterLimit: 0
        }));
    }
}
